/* eslint-disable @typescript-eslint/naming-convention */
import { PredictionError, PredictionInfo } from '../common/prediction.models';
import { TWResponse } from '../common/response.models';

export class PredictionMeasurements {
  timestamps: number[];
  values: number[];
  valuesUB?: number[];
  valuesLB?: number[];
  precision?: number;
}

export class PredictionDisplayData {
  estimation?: PredictionMeasurements;
  prediction?: PredictionMeasurements;
  confidenceInterval?: number;
  eolValue?: number;
  predictionInfoList?: PredictionInfo[];
  error?: PredictionError;
}

export class PredictionDisplayDataResponse {
  data: PredictionDisplayData;
}

export class SpreadValue {
  min: number;
  max: number;
}

export class OperationWindow {
  name: string;
  energy: number;
  voltage_limits: SpreadValue;
  soc_limits: SpreadValue;
  active?: boolean;
}

export class FleetOperationWindowMetadata {
  operation_windows?: OperationWindow[];
}

export class ContainerConfigResponse extends TWResponse {
  data: FleetOperationWindowMetadata;
}

export class ModuleTopologyMetadata {
  entities_in_parallel: number;
  entities_in_series: number;
}

export class ModuleWindowMetadata {
  lower_voltage_limit: number;
  nominal_capacity: number;
  nominal_energy: number;
  nominal_voltage: number;
  upper_voltage_limit: number;
}

export class ModuleMetadata {
  gross_window: ModuleWindowMetadata;
  net_window: ModuleWindowMetadata;
}

export class ContainerMetadata {
  battery_information: {
    chemistry: string;
    manufacturer: string;
    model: string;
    provided_level: 'cell' | 'system';
    cell_level_information: ModuleMetadata;
    system_level_information: ModuleMetadata;
    topology: {
      module_configuration: ModuleTopologyMetadata;
      system_configuration: ModuleTopologyMetadata;
    };
  };
  vehicle_information: {
    manufacturer: string;
    model: string;
  };
  status?: 'DRAFT' | 'RELEASE';
  name?: string;
}

export class FleetContainerMetadataResponse extends TWResponse {
  data: ContainerMetadata;
}

export class VehicleKpiSpread {
  min: number;
  max: number;
  avg: number;
}

export class VehicleKpis {
  end_date: string;
  start_date: string;
  soh: {
    soh: number;
    uncertainty: number;
    x_axis_value: number;
  };
  throughputs: {
    consumption: number;
    efc: number;
    mileage: number;
  };
  aggregations: {
    c_rate: VehicleKpiSpread;
    dod: VehicleKpiSpread;
    soc: VehicleKpiSpread;
    temperature: VehicleKpiSpread;
  };
  remaining_energy: number;
}

export class FleetVehicleKpiResponse extends TWResponse {
  data: VehicleKpis;
}

export class Range {
  maxDod: number;
  safetyMargin: number;
  date: Date;
  remainingEnergy: number;
  availableEnergy: number;
  range: number;
  averageConsumption: number;
  consumption: number;
}

export class RangeMetadata {
  max_dod: number;
  safety_margin: number;
}

export class FleetRangeMetadataResponse extends TWResponse {
  data: RangeMetadata;
}

export class OperationShare {
  start: number;
  end: number;
  shares: { charging: number; relax: number; uptime: number };
}

export class FleetOperationShareResponse extends TWResponse {
  data: OperationShare[];
  error?: string;
}

export class BackendOperationShare {
  start_date: string;
  end_date: string;
  shares: { charging: number; relax: number; uptime: number };
}

export class FleetBackendOperationShareResponse extends TWResponse {
  data: BackendOperationShare[];
  error?: string;
}

export class ChargingShare {
  data: number[][];
}

export class FleetChargingShareResponse extends TWResponse {
  data: ChargingShare;
  error?: string;
}

export class WarrantyProcedure {
  procedure_id: string;
  created_at: string;
  modified_at: string;
  start_date: string;
  end_date: string;
  system_id: string;
  container_id: string;
  status: string;
}

export class WarrantyProcedureCreateResponse extends TWResponse {
  data: WarrantyProcedure;
  error?: string;
}

export class WarrantyProcedureGetAllResponse extends TWResponse {
  data: WarrantyProcedure[];
  error?: string;
}

export class Vehicle {
  container_id: string;
  energy_throughput: number;
  manufacturer: string;
  mileage: number;
  model: string;
  operation_start: string;
  soh: number;
  vehicle_name: string;
}

export class PaginatedVehicles {
  items: Vehicle[];
  page: number;
  page_size: number;
  total_pages: number;
  total_items: number;
  prev: string;
  next: string;
}

export class PaginatedVehiclesResponse extends TWResponse {
  data: PaginatedVehicles;
  error?: string;
}
